import { Injectable, computed, signal } from '@angular/core';
import { Router } from '@angular/router';
import { TabReuseCacheService } from './tab-reuse-cache.service';

export interface ShellTab {
  key: string;
  url: string;
  title: string;
}

@Injectable({ providedIn: 'root' })
export class TabManagerService {
  private static readonly storageKey = 'ain.admin.openTabs.v2';
  private static readonly maxTabs = 10;
  private static readonly maxTitleLength = 120;
  private readonly tabsState = signal<ShellTab[]>([]);
  private readonly activeTabKeyState = signal<string | null>(null);

  readonly tabs = this.tabsState.asReadonly();
  readonly activeTabKey = this.activeTabKeyState.asReadonly();
  readonly hasTabs = computed(() => this.tabsState().length > 0);
  readonly activeTab = computed(() => this.tabsState().find(tab => tab.key === this.activeTabKeyState()) ?? null);

  constructor(
    private readonly cache: TabReuseCacheService,
    private readonly router: Router
  ) {
    this.hydrateFromStorage();
  }

  openOrActivate(url: string, title: string): void {
    const normalizedUrl = this.normalizeUrl(url);
    const normalizedTitle = this.normalizeTitle(title);
    if (!normalizedUrl || normalizedUrl === '/login') {
      return;
    }

    let tabsChanged = false;
    this.tabsState.update(currentTabs => {
      const existingIndex = currentTabs.findIndex(tab => tab.url === normalizedUrl);
      if (existingIndex >= 0) {
        const currentTab = currentTabs[existingIndex];
        if (currentTab.title === normalizedTitle) {
          return currentTabs;
        }

        const updatedTabs = [...currentTabs];
        updatedTabs[existingIndex] = { ...updatedTabs[existingIndex], title: normalizedTitle };
        tabsChanged = true;
        return updatedTabs;
      }

      let nextTabs = [...currentTabs, { key: normalizedUrl, url: normalizedUrl, title: normalizedTitle }];
      if (nextTabs.length > TabManagerService.maxTabs) {
        const droppedTabs = nextTabs.slice(0, nextTabs.length - TabManagerService.maxTabs);
        for (const droppedTab of droppedTabs) {
          this.cache.delete(droppedTab.url);
        }
        nextTabs = nextTabs.slice(-TabManagerService.maxTabs);
      }

      tabsChanged = true;
      return nextTabs;
    });
    const activeChanged = this.activeTabKeyState() !== normalizedUrl;
    if (activeChanged) {
      this.activeTabKeyState.set(normalizedUrl);
    }

    if (tabsChanged || activeChanged) {
      this.persistState();
    }
  }

  updateTabTitle(url: string, title: string): void {
    const normalizedUrl = this.normalizeUrl(url);
    const normalizedTitle = title?.trim();
    if (!normalizedUrl || !normalizedTitle) {
      return;
    }

    let changed = false;
    this.tabsState.update(currentTabs => {
      const nextTabs = currentTabs.map(tab => {
        if (tab.url !== normalizedUrl || tab.title === normalizedTitle) {
          return tab;
        }

        changed = true;
        return { ...tab, title: normalizedTitle };
      });

      // Important: keep the same array reference when nothing changed,
      // otherwise Angular signals re-trigger effects and can create loops.
      return changed ? nextTabs : currentTabs;
    });

    if (changed) {
      this.persistState();
    }
  }

  activateTab(tabKey: string): void {
    const tab = this.tabsState().find(item => item.key === tabKey);
    if (!tab) {
      return;
    }

    this.activeTabKeyState.set(tab.key);
    void this.router.navigateByUrl(tab.url);
    this.persistState();
  }

  closeTab(tabKey: string): void {
    const currentTabs = this.tabsState();
    const targetIndex = currentTabs.findIndex(tab => tab.key === tabKey);
    if (targetIndex < 0) {
      return;
    }

    const [closingTab] = currentTabs.slice(targetIndex, targetIndex + 1);
    const remainingTabs = currentTabs.filter(tab => tab.key !== tabKey);
    const wasActive = this.activeTabKeyState() === tabKey;

    this.tabsState.set(remainingTabs);
    this.cache.delete(closingTab.url);
    this.persistState();

    if (!wasActive) {
      return;
    }

    const fallbackTab = remainingTabs[targetIndex - 1] ?? remainingTabs[targetIndex] ?? null;
    if (fallbackTab) {
      this.activeTabKeyState.set(fallbackTab.key);
      void this.router.navigateByUrl(fallbackTab.url);
      this.persistState();
      return;
    }

    this.activeTabKeyState.set(null);
    void this.router.navigateByUrl('/dashboard');
    this.persistState();
  }

  closeOtherTabs(keepTabKey: string): void {
    const currentTabs = this.tabsState();
    const keepTab = currentTabs.find(tab => tab.key === keepTabKey);
    if (!keepTab) {
      return;
    }

    for (const tab of currentTabs) {
      if (tab.key !== keepTabKey) {
        this.cache.delete(tab.url);
      }
    }

    this.tabsState.set([keepTab]);
    this.activeTabKeyState.set(keepTab.key);
    this.persistState();
    void this.router.navigateByUrl(keepTab.url);
  }

  closeAllTabs(): void {
    for (const tab of this.tabsState()) {
      this.cache.delete(tab.url);
    }

    this.tabsState.set([]);
    this.activeTabKeyState.set(null);
    this.persistState();
    void this.router.navigateByUrl('/dashboard');
  }

  clear(): void {
    this.tabsState.set([]);
    this.activeTabKeyState.set(null);
    this.cache.clear();
    this.persistState();
  }

  private normalizeUrl(url: string): string {
    if (!url) {
      return '';
    }

    const path = url.split(/[?#]/)[0] ?? url;
    return path.startsWith('/') ? path : `/${path}`;
  }

  private normalizeTitle(title: string): string {
    const normalized = title?.trim() || 'Page';
    return normalized.slice(0, TabManagerService.maxTitleLength);
  }

  private hydrateFromStorage(): void {
    try {
      const raw = globalThis.localStorage?.getItem(TabManagerService.storageKey);
      if (!raw) {
        return;
      }

      const parsed = JSON.parse(raw) as { tabs?: ShellTab[]; activeTabKey?: string | null };
      const tabs = this.sanitizeTabs(parsed.tabs);
      const normalizedActive = this.normalizeUrl(parsed.activeTabKey ?? '');
      const activeTabKey = tabs.some(tab => tab.key === normalizedActive) ? normalizedActive : tabs.at(-1)?.key ?? null;

      this.tabsState.set(tabs);
      this.activeTabKeyState.set(activeTabKey);
      this.cache.syncOpenTabUrls(tabs.map(tab => tab.url));

      const expectedNormalizedState = JSON.stringify({ tabs, activeTabKey });
      if (raw !== expectedNormalizedState) {
        globalThis.localStorage?.setItem(TabManagerService.storageKey, expectedNormalizedState);
      }
    } catch {
      this.resetStateAndStorage();
    }
  }

  private persistState(): void {
    this.cache.syncOpenTabUrls(this.tabsState().map(tab => tab.url));
    try {
      globalThis.localStorage?.setItem(
        TabManagerService.storageKey,
        JSON.stringify({
          tabs: this.tabsState(),
          activeTabKey: this.activeTabKeyState()
        })
      );
    } catch {
      // Ignore localStorage write failures.
    }
  }

  private sanitizeTabs(rawTabs: ShellTab[] | undefined): ShellTab[] {
    if (!Array.isArray(rawTabs) || rawTabs.length === 0) {
      return [];
    }

    const result: ShellTab[] = [];
    const seenUrls = new Set<string>();
    for (const tab of rawTabs) {
      const normalizedUrl = this.normalizeUrl(tab?.url ?? tab?.key ?? '');
      if (!normalizedUrl || normalizedUrl === '/login' || seenUrls.has(normalizedUrl)) {
        continue;
      }

      seenUrls.add(normalizedUrl);
      result.push({
        key: normalizedUrl,
        url: normalizedUrl,
        title: this.normalizeTitle(tab?.title ?? '')
      });

      if (result.length >= TabManagerService.maxTabs) {
        break;
      }
    }

    return result;
  }

  private resetStateAndStorage(): void {
    this.tabsState.set([]);
    this.activeTabKeyState.set(null);
    this.cache.clear();
    try {
      globalThis.localStorage?.removeItem(TabManagerService.storageKey);
    } catch {
      // Ignore localStorage cleanup failures.
    }
  }
}
